import { qoursesApi } from '@/api/qourses.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import useGetBooking, { getBookingQueryKey } from '@/hooks/orders/useGetBooking.tsx'
import useGetBookingRevocationEligibility from '@/hooks/orders/useGetBookingRevocationEligibility.tsx'
import { GetBookingsQueryKey } from '@/hooks/orders/useGetBookings.tsx'
import { GetOrderQueryKey } from '@/hooks/orders/useGetOrder.tsx'
import { GetOrderBookingsQueryKey } from '@/hooks/orders/useGetOrderBookings.tsx'
import { GetOrderBookingsWithLineItemQueryKey } from '@/hooks/orders/useGetOrderBookingsWithLineItem.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { Checkbox } from '@/shadcn/components/ui/checkbox.tsx'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shadcn/components/ui/dialog.tsx'
import { Skeleton } from '@/shadcn/components/ui/skeleton.tsx'
import { cn } from '@/shadcn/lib/utils.ts'
import { ToastVariant, minDelay, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookingEntity } from '../../../qourses-api-client'

export default function RevokeBookingModal({ bookingId }: { bookingId: string }) {
  const { t: translate } = useTranslation()
  const queryClient = useQueryClient()

  const [overrideRevocationPolicy, setOverrideRevocationPolicy] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const { booking, isLoading: isLoadingBooking, isError: isErrorBooking } = useGetBooking(bookingId)
  const { bookingRevocationEligibility, isLoading, isError, refetch, isRefetching } =
    useGetBookingRevocationEligibility(bookingId, overrideRevocationPolicy)

  useEffect(() => {
    refetch()
  }, [overrideRevocationPolicy])

  if (isError || isErrorBooking) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('modals.revokeBookingModal.errorState.title')}</DialogTitle>
          <DialogDescription>
            {translate('modals.revokeBookingModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  if (isLoadingBooking || isLoading) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>
            <Skeleton className="h-5 w-3/4" />
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-full" />
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  async function handleRevoke() {
    try {
      setSubmitting(true)
      await minDelay(
        qoursesApi.payment.paymentControllerRevokeBookingIfEligibleV1(
          bookingId,
          overrideRevocationPolicy,
        ),
        800,
      )
      sendNotification(
        translate('modals.revokeBookingModal.notification.title'),
        translate('modals.revokeBookingModal.notification.subtitle'),
        ToastVariant.Success,
      )
    } catch (error) {
      console.error(error)
    } finally {
      setSubmitting(false)
      popAllModals()
      queryClient.invalidateQueries(GetOrderBookingsQueryKey(booking.orderId))
      queryClient.invalidateQueries(GetOrderBookingsWithLineItemQueryKey(booking.orderId))
      queryClient.invalidateQueries(GetOrderQueryKey(booking.orderId))
      queryClient.invalidateQueries(getBookingQueryKey(bookingId))
      queryClient.invalidateQueries(GetBookingsQueryKey())
    }
  }

  const bookingAlreadyRevoked = booking.bookingStatus === BookingEntity.bookingStatus.REVOKED
  const revokeDisabled =
    submitting || !bookingRevocationEligibility.revocable || bookingAlreadyRevoked
  const revocableUntil = bookingRevocationEligibility.revocableUntil
    ? DateTime.fromISO(bookingRevocationEligibility.revocableUntil).toLocaleString(DateTime.DATETIME_MED)
    : null
  const revocationBlockedAt = bookingRevocationEligibility.revocationBlockedAt
    ? DateTime.fromISO(bookingRevocationEligibility.revocationBlockedAt).toLocaleString(
        DateTime.DATETIME_MED,
      )
    : null

  const descriptionKey = bookingAlreadyRevoked
    ? 'alreadyRevoked'
    : bookingRevocationEligibility.revocable
      ? bookingRevocationEligibility.refundRequired
        ? 'revocableRefunded'
        : 'revocableNotNecessary'
      : 'notRevocable'

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-4">
        <DialogTitle className="mt-6 sm:mt-2">
          {translate(`modals.revokeBookingModal.${descriptionKey}.title`)}
        </DialogTitle>
        <DialogDescription className="space-y-2">
          <p>{translate(`modals.revokeBookingModal.${descriptionKey}.subtitle`)}</p>
          {!!revocableUntil && bookingRevocationEligibility.revocable && (
            <p>
              {translate('modals.revokeBookingModal.revocableUntil', {
                date: revocableUntil,
              })}
            </p>
          )}
          {!!revocationBlockedAt && !bookingRevocationEligibility.revocable && (
            <p>
              {translate('modals.revokeBookingModal.revocationBlockedAt', {
                date: revocationBlockedAt,
              })}
            </p>
          )}
          {!!bookingRevocationEligibility.revocationBlockedReason &&
            !bookingRevocationEligibility.revocable && (
              <p>
                {translate(
                  'modals.revokeBookingModal.blockedReasons.' +
                    bookingRevocationEligibility.revocationBlockedReason,
                )}
              </p>
            )}
        </DialogDescription>
      </DialogHeader>
      <div
        className={cn(
          'mb-4 mt-2 flex justify-start gap-x-2 text-xs',
          bookingAlreadyRevoked && 'opacity-50',
        )}
      >
        <Checkbox
          disabled={bookingAlreadyRevoked}
          id="overrideRevocationPolicy"
          checked={overrideRevocationPolicy}
          onClick={() => {
            setOverrideRevocationPolicy(!overrideRevocationPolicy)
          }}
        />
        <label htmlFor="overrideRevocationPolicy" className="text-sm font-medium leading-none">
          {translate('modals.revokeBookingModal.overrideRevocationPolicy.title')}
          <p className="mt-1 text-xs text-gray-600">
            {translate('modals.revokeBookingModal.overrideRevocationPolicy.subtitle')}
          </p>
        </label>
      </div>
      {isRefetching && (
        <div className="mb-4 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-5 w-1/2" />
        </div>
      )}
      <DialogFooter>
        <Button variant="indigo" disabled={revokeDisabled} onClick={handleRevoke}>
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {translate('common.loading')}
            </>
          ) : (
            translate('modals.revokeBookingModal.revokeButton')
          )}
        </Button>
      </DialogFooter>
    </Dynamic.Content>
  )
}
